import React from 'react';
const { useState, useEffect, useRef } = React;
import { callOpenAI, COMPANY_PROFILE } from '../../globals.js';
import { ownerCall } from '../owner/shared.jsx';
import { draftPrompt, hashText, dayLabel, compareWithPlanned, contextOf, visibility } from '../../nextActionRules.js';

// Hoy · panel de seguimiento · próxima acción asistida (rules in src/nextActionRules.js; agreed 04-11-2026).
// The proposal and its draft are prepared together when they apply, once per relevant context (the proposal key):
// reopening the panel reads the stored draft instead of calling the AI again, and Ale's edits are kept. If the AI
// fails, the action proposed by the rules stays available. Nothing is sent: the draft is only a draft.

const muted = { fontSize: 12, color: 'var(--globant-muted)' };
const btn = { fontSize: 12, padding: '4px 10px' };
const box = { border: '1px solid rgba(31,122,112,0.35)', background: 'var(--teal-dim)', borderRadius: 10, padding: 12, display: 'grid', gap: 8 };
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
export const AUTHORIZED_DATA = ['nombre, rol y empresa del contacto', 'canal e idioma (país)', 'tu perfil de remitente', 'fecha, canal y dirección de las interacciones pertinentes (sin su texto)', 'en un primer contacto sin rol ni empresa, la línea que escribas sobre por qué le escribís'];
export const NEVER_SENT = 'Nunca: cuerpos, asuntos ni fragmentos de correos, adjuntos ni notas privadas. Tu nota del panel solo si la marcás.';

export default function NextActionProposal({ proposal, needMotive = false, onChannel = null, openTasks = [], stakeholderId, accountId, contact, company, country, facts, note, statusAfter, busy, onConfirm, onKeep }) {
  const [st, setSt] = useState(null); // { setup, marks, drafts }
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState(null); // { status, text, edited, … }
  const [text, setText] = useState('');
  const [genState, setGenState] = useState(''); // '' | 'working' | 'pending'
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [saveDraft, setSaveDraft] = useState(true);
  const [includeNote, setIncludeNote] = useState(false);
  const [warn, setWarn] = useState('');
  const [dismissing, setDismissing] = useState(false);
  const [motive, setMotive] = useState('');
  const motiveTimer = useRef(null);
  const first = proposal?.rule === 'R0';
  const shownCtx = useRef('');
  const editTimer = useRef(null);
  const key = proposal?.key || '';
  const planned = openTasks.length === 1 ? openTasks[0] : null;
  const compare = planned ? compareWithPlanned(proposal, planned) : null;
  const ctxNow = contextOf(proposal, openTasks, statusAfter);
  // Not again what she already confirmed or discarded, nor what an open action already carries
  const visible = !!st && visibility(proposal, { marks: st.marks || [], openTasks }).show;
  // A draft only when it is useful: the proposal asks for one and it is not already what is planned
  const wantsDraft = !!(proposal?.draft && visible && compare !== 'same' && openTasks.length <= 1);

  // The stored decisions and drafts of this contact (once per contact and proposal key)
  const load = () => ownerCall({ action: 'nextActionsState', stakeholderId, keys: key ? [key] : [] })
    .then(x => { setSt(x); setLoadError(''); const d = key ? x.drafts?.[key] : null; setDraft(d || null); setText(d?.text || ''); setMotive(m => d?.motive || m); })  // the motive follows the contact across channels
    .catch(e => setLoadError(e.message));
  useEffect(() => { if (stakeholderId) load(); }, [stakeholderId, key]);

  // A new proposal: its own title, date and context
  useEffect(() => { setTitle(proposal?.title || ''); setDueOn(proposal?.dueOn || ''); setEditing(false); setWarn(''); shownCtx.current = ctxNow; setIncludeNote(false); }, [key]);

  const prompt = proposal ? draftPrompt({ proposal, contact, company, country, sender: COMPANY_PROFILE || {}, interactions: facts, note, includeNote, motive: first ? motive : '' }) : '';
  // Only the motive was saved (status idle): there is no draft yet
  const hasDraft = !!draft && draft.status !== 'idle';
  const motiveMissing = first && needMotive && !motive.trim();
  const onMotive = (v) => {
    setMotive(v);
    clearTimeout(motiveTimer.current);
    motiveTimer.current = setTimeout(() => ownerCall({ action: 'nextActionDraftContext', stakeholderId, key, motive: v.slice(0, 300) }).then(x => setDraft(d => (d && d.status !== 'idle' ? { ...d, motive: x.draft?.motive || '' } : x.draft))).catch(e => setLoadError(e.message)), 900);
  };

  const generate = async ({ redo = false, retry = false } = {}) => {
    if (!proposal) return;
    const claimId = uid();
    setGenState('working');
    try {
      const c = await ownerCall({ action: 'nextActionDraftClaim', stakeholderId, key, rule: proposal.rule, claim: claimId, redo, retry, ...(first ? { motive } : {}) });
      if (c.disabled) { setGenState(''); return; }
      if (c.draft) { setDraft(c.draft); setText(c.draft.text || ''); if (c.draft.motive) setMotive(c.draft.motive); setGenState(''); return; }
      if (c.pending) { setGenState('pending'); return; }
      let out = null; let failed = '';
      try { out = await callOpenAI({ prompt, max_tokens: 450, temperature: 0.6, method: 'messages', accountId: accountId || undefined }); if (!String(out || '').trim()) failed = 'empty'; }
      catch (e) { failed = 'ai_error'; }
      const s = await ownerCall({ action: 'nextActionDraftSave', key, claim: claimId, ...(failed ? { failed } : { text: String(out).trim().slice(0, 2000), promptHash: hashText(prompt) }) });
      setDraft(s.draft); setText(s.draft?.text || '');
    } catch (e) { setDraft(d => d || { status: 'failed', error: 'store' }); setLoadError(e.message); }
    setGenState('');
  };

  // Prepared automatically, once per key: only when the setup is on, nothing is stored yet and the proposal is stable
  useEffect(() => {
    if (!st?.setup?.enabled || !wantsDraft || hasDraft || genState || motiveMissing) return;
    const t = setTimeout(() => generate(), 1200);
    return () => clearTimeout(t);
  }, [st?.setup?.enabled, wantsDraft, key, hasDraft, genState, motiveMissing]);

  // Ale's edits: kept on the server as she writes them
  const onEdit = (v) => {
    setText(v);
    clearTimeout(editTimer.current);
    editTimer.current = setTimeout(() => ownerCall({ action: 'nextActionDraftEdit', key, text: v }).then(x => setDraft(x.draft)).catch(e => setLoadError(e.message)), 900);
  };
  const flushEdit = async () => {
    clearTimeout(editTimer.current);
    if (hasDraft && text !== draft.text) { const x = await ownerCall({ action: 'nextActionDraftEdit', key, text }); setDraft(x.draft); }
  };

  const setupChoice = async (enabled) => {
    try { const x = await ownerCall({ action: 'nextActionsSetup', enabled }); setSt(s => ({ ...(s || {}), setup: x.setup })); }
    catch (e) { setLoadError(e.message); }
  };

  const dismiss = async (reason) => {
    setDismissing(false);
    try { const x = await ownerCall({ action: 'nextActionDismiss', stakeholderId, key, fp: proposal.fp, reason }); setSt(s => ({ ...(s || {}), marks: [x.mark, ...((s?.marks) || [])] })); if (reason === 'kept') onKeep?.(); }
    catch (e) { setLoadError(e.message); }
  };

  const confirm = async () => {
    setWarn('');
    // The context changed while she reviewed (a new interaction, the planned action changed): say it before saving
    if (shownCtx.current && shownCtx.current !== ctxNow) { shownCtx.current = ctxNow; setWarn('El contexto cambió mientras revisabas (una interacción nueva o la acción planificada cambió). Revisá la propuesta actualizada y confirmá de nuevo.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueOn) || !title.trim()) { setWarn('Falta la acción o la fecha.'); return; }
    try { await flushEdit(); } catch (e) { setWarn(`No se pudo guardar tu edición del borrador: ${e.message}`); return; }
    const edited = title.trim() !== proposal.title || dueOn !== proposal.dueOn || !!draft?.edited;
    // The message travels with the task as its current version (Source.draft), never copied into the notes
    const message = saveDraft && draft?.status === 'ready' && text.trim() ? { text: text.trim(), channel: proposal.channel || 'Email', edited: !!draft?.edited } : null;
    const suggestion = { rule: proposal.rule, version: proposal.version, key: proposal.key, fp: proposal.fp, edited };
    onConfirm(planned && compare === 'different'
      ? { mode: 'update', taskId: planned.id, expectUpdatedAt: planned.updatedAt || '', title: title.trim(), dueOn, draft: message, suggestion, ctx: ctxNow }
      : { mode: 'new', title: title.trim(), dueOn, draft: message, suggestion, ctx: ctxNow });
  };

  if (!proposal) return null;
  if (loadError && !st) return <div role="alert" style={{ fontSize: 12, color: '#B4362C' }}>No se pudo leer la propuesta de Oike: {loadError} <button type="button" className="action-btn" style={btn} onClick={load}>Reintentar</button></div>;
  if (!visible) return null;
  if (openTasks.length > 1) return <div style={muted}>Oike tiene una sugerencia ({proposal.label}), pero hay varias acciones abiertas para este contacto: resolvelas antes.</div>;
  if (compare === 'same') return <div style={muted}>La acción planificada coincide con la sugerencia de Oike ({proposal.label}): no hace falta cambiar nada.</div>;

  const setup = st?.setup;
  return (
    <section aria-label="Propuesta de Oike" style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <b style={{ fontSize: 13, color: '#155C54' }}>{first ? 'Preparar primer contacto' : `Propuesta de Oike · ${proposal.label}`}</b>
        <span style={muted}>regla {proposal.rule} · v{proposal.version}</span>
      </div>

      {planned && compare === 'different' && (
        <div style={{ fontSize: 12, color: '#7A4E0E' }}>Ya tenés planificado «{planned.title}» para el {planned.dueOn ? dayLabel(planned.dueOn) : 'sin fecha'}. Oike no lo cambia: podés mantenerlo o actualizar esa misma acción.</div>
      )}

      {editing ? (
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Acción<input className="input-field" style={{ display: 'block', width: '100%', boxSizing: 'border-box', fontSize: 13, marginTop: 2 }} value={title} maxLength={200} onChange={e => setTitle(e.target.value)} /></label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Fecha<input type="date" className="input-field" style={{ display: 'block', fontSize: 13, marginTop: 2, width: 'auto' }} value={dueOn} onChange={e => setDueOn(e.target.value)} /></label>
        </div>
      ) : (
        <div style={{ fontSize: 13, display: 'grid', gap: 2 }}>
          <div><b>Acción:</b> {title}</div>
          <div><b>Fecha sugerida:</b> {dueOn ? dayLabel(dueOn) : '—'}{proposal.clamped && dueOn === proposal.dueOn ? <span style={muted}> (correspondía el {dayLabel(proposal.ideal)}; nunca se propone una fecha pasada)</span> : null}</div>
        </div>
      )}
      <div style={{ fontSize: 13 }}><b>Motivo:</b> {proposal.reason}</div>
      {first && (
        <div role="group" aria-label="Canal del primer contacto" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
          <span style={{ fontWeight: 600 }}>Canal:</span>
          {proposal.channels.map(c => (
            <button key={c} type="button" className="action-btn" aria-pressed={proposal.channel === c} onClick={() => onChannel?.(c)}
              style={{ ...btn, ...(proposal.channel === c ? { background: 'var(--globant-card)', borderColor: 'var(--globant-green)', color: '#155C54' } : {}) }}>{c === 'Email' ? 'Correo' : c}</button>
          ))}
          <span style={muted}>Cada canal guarda su propio borrador.</span>
        </div>
      )}
      {first && needMotive && (
        <label style={{ fontSize: 12, fontWeight: 600, display: 'grid', gap: 2 }}>¿Por qué le escribís? <span style={{ fontWeight: 400, color: 'var(--globant-muted)' }}>Falta contexto para redactar: no tiene rol ni empresa. Una línea alcanza; solo se usa para este borrador.</span>
          <input className="input-field" style={{ fontSize: 13 }} value={motive} maxLength={300} onChange={e => onMotive(e.target.value)} placeholder="Ej.: nos conocimos en el webinar de octubre y le interesó ordenar su prospección" />
        </label>
      )}
      {proposal.basis.length > 0 && <div style={muted}>Basado en: {proposal.basis.map(b => (b.pending ? 'lo que registrás ahora' : dayLabel(b.date))).join(' · ')}{proposal.channel ? ` · canal ${proposal.channel}` : ''}{proposal.alternatives.length ? ` · también podés usar ${proposal.alternatives.join(' o ')} (no se elige solo)` : ''}</div>}
      {proposal.missing.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#7A4E0E' }} aria-label="Falta o conviene revisar">
          {proposal.missing.map(m => <li key={m}>{m}</li>)}
        </ul>
      )}

      {/* The draft: prepared together with the proposal, once; it is a draft, never counted as sent */}
      {proposal.draft && (
        <div style={{ borderTop: '1px solid rgba(31,122,112,0.25)', paddingTop: 8, display: 'grid', gap: 6 }}>
          {!st && !loadError && <div style={muted}>Cargando…</div>}
          {st && !setup && (
            <div style={{ fontSize: 12, display: 'grid', gap: 6 }}>
              <b>¿Preparo los borradores automáticamente?</b>
              <span>Cuando una propuesta lleva mensaje, Oike prepara el borrador una sola vez con: {AUTHORIZED_DATA.join('; ')}.</span>
              <span style={muted}>{NEVER_SENT} Nada se envía: queda como borrador. Podés apagarlo cuando quieras.</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button type="button" className="action-btn btn-primary" style={btn} onClick={() => setupChoice(true)}>Activar borradores automáticos</button>
                <button type="button" className="action-btn" style={btn} onClick={() => setupChoice(false)}>Ahora no</button>
              </div>
            </div>
          )}
          {st && setup && !setup.enabled && !hasDraft && (
            <div style={{ ...muted, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              Borradores automáticos apagados.
              <button type="button" className="action-btn" style={btn} disabled={!!genState || motiveMissing} onClick={() => generate({ retry: true })}>Preparar este borrador</button>
              <button type="button" className="action-btn" style={btn} onClick={() => setupChoice(true)}>Activar</button>
            </div>
          )}
          {motiveMissing && setup?.enabled && !hasDraft && <div style={{ fontSize: 12, color: '#7A4E0E' }}>El borrador se prepara cuando escribas por qué le escribís.</div>}
          {genState === 'working' && <div role="status" style={muted}>Preparando el borrador…</div>}
          {genState === 'pending' && <div role="status" style={muted}>Se está preparando en otra pestaña. <button type="button" className="action-btn" style={btn} onClick={() => { setGenState(''); load(); }}>Actualizar</button></div>}
          {draft?.status === 'pending' && !genState && <div role="status" style={muted}>Preparándose… <button type="button" className="action-btn" style={btn} onClick={load}>Actualizar</button></div>}
          {draft?.status === 'failed' && !genState && (
            <div style={{ fontSize: 12, color: '#7A4E0E', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              No se pudo preparar el borrador. La acción sugerida sigue disponible.
              <button type="button" className="action-btn" style={btn} onClick={() => generate({ retry: true })}>Reintentar</button>
            </div>
          )}
          {draft?.status === 'ready' && (
            <>
              <label style={{ fontSize: 12, fontWeight: 700 }}>Borrador del mensaje <span style={{ fontWeight: 400, color: 'var(--globant-muted)' }}>(no enviado{draft.edited ? ' · editado por vos' : ''})</span>
                <textarea className="input-field" style={{ display: 'block', width: '100%', minHeight: 120, marginTop: 4, fontSize: 13, lineHeight: 1.5, boxSizing: 'border-box', fontFamily: 'inherit' }}
                  value={text} maxLength={2000} onChange={e => onEdit(e.target.value)} onBlur={() => flushEdit().catch(e => setLoadError(e.message))} />
              </label>
              <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={saveDraft} onChange={e => setSaveDraft(e.target.checked)} /> Guardar el mensaje en la tarea al confirmar (lo abrís después con «Ver mensaje»; no toca sus notas)
              </label>
            </>
          )}
          {first && needMotive && hasDraft && draft.status === 'ready' && motive.trim() && motive.trim() !== String(draft.motiveUsed ?? '').trim() && (
            <button type="button" className="action-btn" style={{ ...btn, justifySelf: 'start' }} disabled={!!genState}
              onClick={() => { if (draft?.edited && !window.confirm('Rehacer reemplaza tu borrador editado. ¿Seguir?')) return; generate({ redo: true }); }}>Rehacer el borrador con este motivo</button>
          )}
          {String(note || '').trim() && setup && (
            <div style={{ fontSize: 12, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={includeNote} onChange={e => setIncludeNote(e.target.checked)} /> Incluir mi nota del panel</label>
              {includeNote && <button type="button" className="action-btn" style={btn} disabled={!!genState}
                onClick={() => { if (draft?.edited && !window.confirm('Rehacer reemplaza tu borrador editado. ¿Seguir?')) return; generate({ redo: true }); }}>Rehacer el borrador con mi nota</button>}
            </div>
          )}
          {(hasDraft || setup) && (
            <details style={{ fontSize: 12 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--globant-green)', fontWeight: 600 }}>Qué se envía a la IA</summary>
              <div style={{ ...muted, margin: '4px 0' }}>Tal como se envía (más las herramientas activas de tu biblioteca del método para mensajes, que agrega el servidor). {NEVER_SENT}</div>
              {draft?.promptHash && draft.promptHash !== hashText(prompt) && <div style={{ color: '#7A4E0E' }}>Los datos cambiaron desde que se preparó este borrador.</div>}
              <pre style={{ whiteSpace: 'pre-wrap', background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 8, padding: 8, margin: 0, fontSize: 11 }}>{prompt}</pre>
              {setup?.enabled && <button type="button" className="action-btn" style={{ ...btn, marginTop: 6 }} onClick={() => setupChoice(false)}>Apagar borradores automáticos</button>}
            </details>
          )}
        </div>
      )}

      {loadError && <div role="alert" style={{ fontSize: 12, color: '#B4362C' }}>{loadError}</div>}
      {warn && <div role="alert" style={{ fontSize: 12, color: '#7A4E0E' }}>{warn}</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="action-btn btn-primary" style={btn} disabled={busy} onClick={confirm}>{planned ? 'Actualizar esa acción' : 'Confirmar'}</button>
        <button type="button" className="action-btn" style={btn} onClick={() => setEditing(v => !v)} aria-pressed={editing}>{editing ? 'Listo' : 'Editar'}</button>
        {planned && <button type="button" className="action-btn" style={btn} onClick={() => dismiss('kept')}>Mantener lo planificado</button>}
        {!dismissing ? <button type="button" className="action-btn" style={btn} onClick={() => setDismissing(true)}>Descartar</button> : (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
            <span style={muted}>¿Por qué?</span>
            <button type="button" className="action-btn" style={btn} onClick={() => dismiss('resolved')}>Ya lo resolví</button>
            <button type="button" className="action-btn" style={btn} onClick={() => dismiss('not_relevant')}>No corresponde</button>
            <button type="button" className="action-btn" style={btn} onClick={() => dismiss('later')}>Más adelante</button>
            <button type="button" className="action-btn" style={btn} onClick={() => dismiss('')}>Sin motivo</button>
          </span>
        )}
      </div>
    </section>
  );
}
